import { Panel, Chip } from "@/components/lux";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyField, MiniToggle, Line, Divider } from "./fields";
import { Receipt, Plus, X, Percent } from "lucide-react";

export default function FeesPanel({ ws, set, totals, settings }) {
  const fees = ws.fees || [];
  const update = (i, patch) => set({ fees: fees.map((f, k) => (k === i ? { ...f, ...patch } : f)) });
  const add = () => set({ fees: [...fees, { id: `custom_${Date.now()}`, label: "Custom fee", amount: 0, taxable: true, enabled: true }] });
  const remove = (i) => set({ fees: fees.filter((_, k) => k !== i) });
  const luxAuto = totals.luxury_tax > 0 && (ws.luxury_tax_override === null || ws.luxury_tax_override === undefined);

  return (
    <Panel
      label="Fees & taxes" title="Fees, HST & luxury tax" icon={Receipt} testid="desk-fees-panel"
      actions={<Button size="sm" variant="outline" onClick={add} data-testid="desk-add-fee"><Plus className="h-3.5 w-3.5 mr-1" />Fee</Button>}
    >
      <div className="space-y-2">
        {fees.map((f, i) => (
          <div key={f.id || i} className="flex items-center gap-2" data-testid={`desk-fee-row-${f.id || i}`}>
            <input
              type="checkbox" checked={f.enabled !== false} onChange={(e) => update(i, { enabled: e.target.checked })}
              className="accent-[hsl(var(--gold))] shrink-0" data-testid={`desk-fee-enabled-${f.id || i}`}
            />
            <Input
              value={f.label} onChange={(e) => update(i, { label: e.target.value })}
              className={`h-9 flex-1 ${f.enabled === false ? "opacity-50" : ""}`} data-testid={`desk-fee-label-${f.id || i}`}
            />
            <MiniToggle
              on={f.taxable !== false} onClick={() => update(i, { taxable: f.taxable === false })}
              onLabel="Taxable" offLabel="No tax" testid={`desk-fee-taxable-${f.id || i}`} title="Toggle HST on this fee"
            />
            <div className="w-28 shrink-0"><MoneyField value={f.amount} onChange={(v) => update(i, { amount: v })} testid={`desk-fee-amount-${f.id || i}`} /></div>
            <button onClick={() => remove(i)} className="p-1.5 rounded-full text-muted-foreground hover:text-red-400 shrink-0" title="Remove fee" data-testid={`desk-fee-remove-${f.id || i}`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}

        <Divider />
        <div className="flex items-center gap-3 flex-wrap">
          <Chip active={!ws.tax_exempt} onClick={() => set({ tax_exempt: false })} testid="desk-tax-on">HST applies</Chip>
          <Chip active={!!ws.tax_exempt} onClick={() => set({ tax_exempt: true })} testid="desk-tax-exempt">Tax exempt</Chip>
          <div className="flex items-center gap-2">
            <span className="label-mono">Rate</span>
            <div className="relative w-24">
              <Input
                type="number" step="0.01" value={ws.tax_rate ?? 13} onChange={(e) => set({ tax_rate: Number(e.target.value) })}
                className="h-9 pr-7 text-right tnum" data-testid="desk-tax-rate"
              />
              <Percent className="h-3 w-3 absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            </div>
          </div>
        </div>
        {ws.tax_exempt && (
          <Input
            value={ws.tax_note || ""} onChange={(e) => set({ tax_note: e.target.value })}
            placeholder="Reason / out-of-province note (prints on the bill)" className="h-9" data-testid="desk-tax-note"
          />
        )}

        {(totals.luxury_tax > 0 || ws.luxury_tax_override) && (
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.05] p-3.5 space-y-2" data-testid="desk-luxury-block">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="label-mono text-amber-300">Federal luxury tax</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {luxAuto ? `Auto-suggested — vehicles over ${Number(settings?.luxury_tax?.threshold || 100000).toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 })}` : "Manual override"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-28"><MoneyField value={totals.luxury_tax} onChange={(v) => set({ luxury_tax_override: v })} testid="desk-luxury-amount" /></div>
                <Button size="sm" variant="ghost" onClick={() => set({ luxury_tax_enabled: false, luxury_tax_override: 0 })} data-testid="desk-luxury-remove">Remove</Button>
              </div>
            </div>
          </div>
        )}

        <Divider />
        <Line label="Taxable fees" value={totals.fees_taxable} muted />
        <Line label="Non-taxable fees" value={totals.fees_non_taxable} muted />
        <Line label="Taxable base" value={totals.taxable_base} hint="Price − trade allowance + taxable fees & products" />
        <Line label={`HST ${totals.tax_exempt ? "(exempt)" : `${totals.tax_rate}%`}`} value={totals.tax_exempt ? "No tax" : totals.tax} bold gold testid="desk-tax-amount" />
      </div>
    </Panel>
  );
}
